<template>
    <teleport :to="props.to" defer v-if="props.tags.length && props.to">
        <ul
            ref="filterTagsMeasureRef"
            class="header-right-filter-tags is-measure"
            aria-hidden="true"
        >
            <li
                v-for="tag in props.tags"
                :key="`filter-tag-measure-${tag}`"
                class="filter-tag"
            >
                <primevue-tag :value="tag" />
            </li>
            <li class="filter-tag more-tags">
                <primevue-tag value="..." />
            </li>
        </ul>
        <ul class="header-right-filter-tags">
            <li
                v-for="tag in props.tags.slice(0, maxVisibleFilterTags)"
                :key="`filter-tag-${tag}`"
                class="filter-tag"
            >
                <primevue-tag :value="tag" />
            </li>
            <li
                v-if="props.tags.length > maxVisibleFilterTags"
                class="filter-tag more-tags"
            >
                <primevue-tag
                    v-tooltip="props.tags.slice(maxVisibleFilterTags, props.tags.length).join(', ')"
                    value="..."
                />
            </li>
        </ul>
    </teleport>
</template>

<script lang="ts" setup>
import {
    nextTick, onBeforeUnmount, ref, watch,
} from 'vue';
import PrimevueTag from 'primevue/tag';

const props = defineProps<{
    tags: Array<string>;
    to: HTMLElement;
}>();

const ICON_HOVER_WIDTH_REM = 4;
const WIDTH_RESERVE_ICON_SELECTORS = [
    '[data-filter-tags-width-reserve]',
    '[data-module-move-icon]',
] as const;

const filterTagsMeasureRef = ref<HTMLUListElement | null>(null);
const maxVisibleFilterTags = ref<number>(1);
let filterTagsResizeObserver: ResizeObserver | null = null;

function disconnectFilterTagsResizeObserver (): void {
    if (!filterTagsResizeObserver) return;
    filterTagsResizeObserver.disconnect();
    filterTagsResizeObserver = null;
}

function getAvailableFilterTagsWidth (host: HTMLElement): number {
    let availableWidth = host.clientWidth;
    const scope = host.closest('.chart-module-header') ?? host.parentElement;
    if (!scope) return availableWidth;

    const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const iconReservePx = ICON_HOVER_WIDTH_REM * remPx;

    WIDTH_RESERVE_ICON_SELECTORS.forEach((selector) => {
        const icon = scope.querySelector(selector);
        if (!(icon instanceof HTMLElement)) return;
        if (icon.offsetWidth > 0) return;
        availableWidth -= iconReservePx;
    });

    return availableWidth;
}

function recomputeMaxVisibleFilterTags (): void {
    const measureList = filterTagsMeasureRef.value;
    if (!measureList) return;

    const availableWidth = getAvailableFilterTagsWidth(props.to);
    if (availableWidth <= 0) {
        maxVisibleFilterTags.value = 0;
        return;
    }

    const measureChildren = Array.from(measureList.children);
    const tagItems = measureChildren.filter(
        (el): el is HTMLElement => el instanceof HTMLElement && !el.classList.contains('more-tags'),
    );
    const moreItem = measureChildren.find(
        (el): el is HTMLElement => el instanceof HTMLElement && el.classList.contains('more-tags'),
    );
    const moreWidth = moreItem?.offsetWidth ?? 0;
    const measureStyles = getComputedStyle(measureList);
    const gap = parseFloat(measureStyles.columnGap || measureStyles.gap) || 0;

    let usedWidth = 0;
    let visibleCount = 0;

    tagItems.some((item, index) => {
        const tagWidth = item.offsetWidth;
        const gapBefore = visibleCount > 0 ? gap : 0;
        const remainingAfterThis = tagItems.length - index - 1;
        const moreSpaceNeeded = remainingAfterThis > 0 ? gap + moreWidth : 0;
        const widthIfIncluded = usedWidth + gapBefore + tagWidth;

        if (widthIfIncluded + moreSpaceNeeded > availableWidth) {
            return true;
        }

        usedWidth = widthIfIncluded;
        visibleCount += 1;
        return false;
    });

    if (visibleCount === 0 && tagItems.length > 0) {
        maxVisibleFilterTags.value = 1;
        return;
    }

    maxVisibleFilterTags.value = visibleCount;
}

async function scheduleRecomputeMaxVisibleFilterTags (): Promise<void> {
    await nextTick();
    await nextTick();
    await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
    });
    recomputeMaxVisibleFilterTags();
}

function attachFilterTagsResizeObserver (): void {
    disconnectFilterTagsResizeObserver();
    if (!props.to) return;

    const resizeRoot = props.to.closest('.module-outer')
        ?? props.to.closest('.module-wrapper')
        ?? props.to;

    filterTagsResizeObserver = new ResizeObserver(() => {
        recomputeMaxVisibleFilterTags();
    });
    filterTagsResizeObserver.observe(resizeRoot);
}

watch(() => props.to, () => {
    if (props.to) {
        attachFilterTagsResizeObserver();
        scheduleRecomputeMaxVisibleFilterTags();
        return;
    }
    disconnectFilterTagsResizeObserver();
}, { immediate: true });

watch(() => props.tags.length, () => {
    scheduleRecomputeMaxVisibleFilterTags();
});

watch(filterTagsMeasureRef, () => {
    scheduleRecomputeMaxVisibleFilterTags();
});

onBeforeUnmount(() => {
    disconnectFilterTagsResizeObserver();
});
</script>

<style lang="scss" scoped>

.header-right-filter-tags {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    width: 100%;
    gap: var(--smallSpacing);
    min-width: min-content;

    &.is-measure {
        position: absolute;
        visibility: hidden;
        pointer-events: none;
        width: max-content;
        height: auto;
        left: 0;
        top: 0;
        z-index: -1;
    }

    .filter-tag {
        flex-shrink: 0;
        white-space: nowrap;
    }
}

</style>
